import { toast } from '@atrvd/saas-ui'
import {
  useCreateBankAccounts,
  useDeleteBankAccount,
  useRenameBankInstitution,
  useUpdateBankAccount,
} from '@/hooks/useBanking'
import type { ManualBankFormMode, ManualBankFormValues } from './manualBankSchema'
import { isEmptySavePlan, planManualBankSave, type SeededManualAccount } from './manualBankSave'

interface SaveCallbacks {
  onClose: () => void
  /**
   * Called with the bank's stored name once it is final — after a successful
   * save, and also when some writes landed but another failed (the dialog
   * then closes too), so a caller addressing the bank by name (the detail
   * page's URL) can follow a rename.
   */
  onSaved?: (institutionName: string) => void
}

/**
 * Persist the manual bank form for its mode, reporting the outcome by toast
 * (raw error message, ADR 0006). `save` resolves only once every write has
 * settled, so the caller derives its saving state from that promise — not from
 * the mutations' `isPending`, which tracks only each mutation's latest call and
 * drops while earlier concurrent writes of the same mutation are in flight.
 *
 * Edit mode: rename first (it rewrites the bank's accounts, so it must land
 * before the per-account writes), then update only the changed rows, create
 * the new ones and soft-delete the removed ones.
 */
export function useSaveManualBank(mode: ManualBankFormMode, { onClose, onSaved }: SaveCallbacks) {
  const createAccounts = useCreateBankAccounts()
  const updateAccount = useUpdateBankAccount()
  const deleteAccount = useDeleteBankAccount()
  const renameInstitution = useRenameBankInstitution()

  /** Create a batch of accounts under `name` and report it. */
  const createUnder = async (name: string, values: ManualBankFormValues) => {
    await createAccounts.mutateAsync(
      values.accounts.map((row) => ({
        name: row.name,
        institution_name: name,
        account_type: row.account_type,
        account_number: row.account_number,
        facility_id: row.facility_id,
      })),
    )
    const count = values.accounts.length
    toast.success(`${count} account${count === 1 ? '' : 's'} created for "${name}"`)
    onSaved?.(name)
    onClose()
  }

  /**
   * Apply an edit. The per-account writes are independent, so all of them are
   * allowed to finish before the outcome is decided:
   * - nothing landed (every write failed, no rename) → throw, the dialog stays
   *   open and a retry re-sends exactly the same writes;
   * - anything landed → report the first error but close: the form was seeded
   *   once, so resubmitting it would re-create rows that now exist (manual
   *   rows have no unique guard). Reopening reseeds from fresh data. The
   *   caller still gets the final name, since a rename may have moved the bank.
   */
  const applyEdit = async (
    storedName: string,
    values: ManualBankFormValues,
    original: SeededManualAccount[],
  ) => {
    const plan = planManualBankSave(storedName, values.institution_name, original, values.accounts)
    if (isEmptySavePlan(plan)) {
      onClose()
      return
    }
    // renameInstitution rewrites EVERY manual row of the exact name, also one
    // added since the form was seeded, so no account stays behind as a stray
    // bank under the old name.
    if (plan.rename) await renameInstitution.mutateAsync(plan.rename)
    // Distinct account ids: these writes are independent of each other.
    const results = await Promise.allSettled([
      ...plan.updates.map((update) => updateAccount.mutateAsync(update)),
      ...(plan.creates.length > 0 ? [createAccounts.mutateAsync(plan.creates)] : []),
      ...plan.deletedIds.map((id) => deleteAccount.mutateAsync(id)),
    ])
    const failure = results.find((result) => result.status === 'rejected')
    if (failure) {
      const landed = Boolean(plan.rename) || results.some((result) => result.status === 'fulfilled')
      if (!landed) throw failure.reason
      toast.error(
        failure.reason instanceof Error ? failure.reason.message : 'Failed to save the financial account',
      )
    } else {
      toast.success(`"${plan.finalName}" updated`)
    }
    onSaved?.(plan.finalName)
    onClose()
  }

  const save = async (values: ManualBankFormValues, original: SeededManualAccount[]) => {
    try {
      if (mode.kind === 'create') await createUnder(values.institution_name, values)
      // The exact stored name, so the new accounts join the bank's group.
      else if (mode.kind === 'add-accounts') await createUnder(mode.institutionName, values)
      else await applyEdit(mode.institutionName, values, original)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save the financial account')
    }
  }

  return { save }
}
